// Client Executive Summary — Wizard.
//
// 4-step MUI Stepper that lets the sales / proposal team turn D-Tools
// proposals into a branded client-facing executive summary PDF.
//
// Backend contract lives in server/routes/client-executive-summary.js.
// All endpoints under /api/exec-summary and require the axios auth interceptor
// wired up in ../../services/api.
//
// Design lock: navy #1A5490 primary, peach #FF6B35 quiet accent only.
// Match BuildV1Preview.js visual vocabulary.
//
// Turn 1: scaffold + Step 1 (Upload/Parse).
// Turn 2: Step 2 (Header + disciplines editor).  <-- current state
// Turn 3: Step 3 (Imagery). Turn 4: Step 4 (Review + generate + drafts).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card,
  CardActionArea, CardContent, Chip, CircularProgress, Divider, FormControlLabel,
  Grid, IconButton, InputAdornment, MenuItem, Paper, Select, Stack, Step,
  StepLabel, Stepper, Switch, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Tabs, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  CheckCircle as OkIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as PdfIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Error as ErrIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PictureAsPdfIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// -------------------------------------------------------------
// Brand + constants
// -------------------------------------------------------------
const NAVY = '#1A5490';
const PEACH = '#FF6B35'; // quiet accent only — brighten switch + subtle highlights

const STEPS = [
  'Upload proposals',
  'Header & disciplines',
  'Imagery',
  'Review & generate',
];

// Discipline templates — seed values for each of the four canonical
// disciplines. `experience_lede`, `stats`, and `system_items` are the
// narrative fields the renderer needs to produce a v1-quality deck (matches
// the 4 East 66th baseline). Server-side parsers auto-populate the numeric
// fields (stats numbers, room counts); the salesperson edits the copy.
const DISCIPLINE_TEMPLATES = {
  avit: {
    id: 'avit',
    eyebrow: 'Audio, Video & Networking',
    title: 'AV & IT',
    tag: 'Designed and engineered as a single instrument',
    experience_label: 'The Experience',
    experience_lede:
      'Every room speaks the same language. Music drifts from one space to the next without a touch. ' +
      'Cinema wakes for an evening film. Throughout the residence, connectivity is invisible, instantaneous, ' +
      'and invariably present.',
    image_caption: 'Theatre, on demand',
    // Placeholder stat labels. These are overwritten by the server-side
    // parser's suggestions.stats (see build_suggestions in
    // server/exec-summary/parse_proposal.py) when a proposal is uploaded.
    // If they survive to render time it means the parser produced no stats,
    // in which case the wizard should let the salesperson fill them in.
    stats: [
      { num: '', lab: 'Displays' },
      { num: '', lab: 'Audio Zones' },
      { num: '', lab: 'Rooms Designed' },
    ],
    system_items: [
      'Distributed audio across the residence, paired with architectural loudspeakers',
      'Premium displays throughout the primary living spaces',
      'Dedicated cinematic listening in the media room',
      'Streaming sources on articulating in-wall mounts',
      'Enterprise-grade perimeter security and cloud-managed wireless',
      'Structured wired backbone with dedicated head-end rack',
      'Proactive remote monitoring \u2014 issues seen before you do',
    ],
    rooms_intro: 'Where you will find sound, picture, and connectivity throughout the residence.',
    rooms: [],
    network_block: {
      label: 'Network & Infrastructure',
      title: 'Connectivity throughout the residence.',
      items: [
        { head: 'Perimeter Security', body: 'Next-generation firewall with Gateway Security \u2014 a hardened threshold between the residence and the public internet.' },
        { head: 'Wireless Coverage',  body: 'Wi-Fi 6 access points, cloud-managed \u2014 whole-residence coverage including primary suites and dressing rooms.' },
        { head: 'Wired Backbone',     body: 'Managed PoE+ AV switching with dedicated head-end racks, IP-monitored power conditioning, and active cooling.' },
        { head: 'Proactive Monitoring', body: 'Remote-access appliance and lifetime licensing \u2014 our team sees issues before you do.' },
      ],
    },
    // Placeholder summary strip. Same override behavior as stats — the
    // parser fills real numbers/labels from the proposal on upload.
    summary_strip: [
      { num: '', lab: 'Displays' },
      { num: '', lab: 'Audio Zones' },
      { num: '', lab: 'Architectural Speakers' },
      { num: 'Whole', lab: 'Home Wi-Fi 6 Network' },
    ],
    subtotal: 0,
  },
  lighting: {
    id: 'lighting',
    eyebrow: 'Lutron HomeWorks QSX',
    title: 'Lighting Control',
    tag: 'A single, intuitive language of touch',
    experience_label: 'The Experience',
    experience_lede:
      'Every room reveals itself in light tuned for the moment. Engraved keypads place the entire residence ' +
      'under a single, considered gesture \u2014 a quiet replacement for the switch banks that once cluttered the walls.',
    image_caption: 'A single room, every hour of the day',
    stats: [
      { num: '', lab: 'Engraved Palladiom Keypads' },
      { num: '', lab: 'Phase-Adaptive Modules' },
      { num: 'Whole', lab: 'Home, Tuned by Scene' },
    ],
    system_items: [
      'Lutron HomeWorks QSX \u2014 the flagship residential lighting platform',
      'Engraved Palladiom keypads with scene labeling',
      'Phase-adaptive dimming for every fixture, every load',
      'Pre-set scenes for each room, tuned during commissioning',
      'Follow-up walk-through to refine after occupancy',
    ],
    subtotal: 0,
  },
  fixtures: {
    id: 'fixtures',
    eyebrow: 'Architectural Lighting',
    title: 'Lighting Fixtures',
    tag: 'Specification-grade fixtures, coordinated end-to-end',
    experience_label: 'The Scope',
    experience_lede:
      'Architectural fixtures procured, staged, and delivered to site in coordination with the electrical trade. ' +
      'Selections align with the lighting design intent and the residence-wide control system.',
    stats: [],
    system_items: [
      'Specification-grade fixture selections coordinated with the designer of record',
      'Consolidated procurement across manufacturers, single point of accountability',
      'Staged delivery aligned to the site schedule',
      'Full compatibility with the Lutron control platform',
    ],
    no_equipment: true,
    subtotal: 0,
  },
  shades: {
    id: 'shades',
    eyebrow: 'Lutron Sivoia QS Wired',
    title: 'Motorized Window Treatments',
    tag: 'Daylight, on command',
    experience_label: 'The Experience',
    experience_lede:
      'Daylight is given a place at the table. Roller shades draw in silence, by scene or by hand, across the ' +
      'principal rooms and the private quarters. Solar fabrics soften the afternoon. Blackouts secure the night. ' +
      'Nothing is asked of the rooms beyond a thought.',
    image_caption: 'View preserved. Glare resolved.',
    stats: [
      { num: '', lab: 'Motorized Shades' },
      { num: '', lab: 'Solar Shades' },
      { num: '', lab: 'Blackout Shades' },
    ],
    system_items: [
      'Lutron Sivoia QS Wired roller shades, integrated with the HomeWorks lighting platform',
      'Solar fabrics tuned for glare control and view retention',
      'Blackout fabrics for private quarters and media spaces',
      'Coordinated room-by-room scenes \u2014 open, half, privacy, blackout',
      'Whisper-quiet motors, mains-powered where possible',
    ],
    rooms_intro: 'Every shaded opening in the residence, by room and by fabric.',
    rooms: [],
    rooms_footnote: 'Solar shades chosen for glare mitigation while preserving the view. Blackout shades chosen for full light exclusion. All openings on motorized control with coordinated scenes.',
    summary_strip: [
      { num: '', lab: 'Solar Shades' },
      { num: '', lab: 'Blackout Shades' },
      { num: '', lab: 'Rooms Shaded' },
      { num: '', lab: 'Coordinated Scenes' },
    ],
    subtotal: 0,
  },
};

const DEFAULT_COVER_EYEBROW = 'A Technology Solution Presented For';

// -------------------------------------------------------------
// Helpers
// -------------------------------------------------------------
function formatCurrency(n) {
  const v = Number(n);
  if (!isFinite(v)) return '$0';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'discipline';
}

function inferDisciplineId(str) {
  const s = String(str || '').toLowerCase();
  if (!s) return null;
  // Server-side parser returns canonical ids already — pass through.
  if (['avit', 'lighting', 'fixtures', 'shades'].includes(s)) return s;
  // Order matters — check the more specific patterns first.
  if (/fixture/.test(s)) return 'fixtures';
  if (/(shade|window|drapery|treatment)/.test(s)) return 'shades';
  if (/light/.test(s)) return 'lighting';
  if (/(av|audio|video|network|it\b)/.test(s)) return 'avit';
  return slugify(str);
}

// Normalize a parsed rooms[] array to the shape our renderer + editors expect.
// Server parsers may return richer objects (avit rooms carry control/network/
// product_count; shades rooms carry floor/code/side_channels/note). Strip to
// the renderer's schema so wizard state stays lean and the editor UI stays
// clean.
function normalizeRooms(disciplineId, rooms) {
  if (!Array.isArray(rooms)) return [];
  if (disciplineId === 'avit') {
    return rooms
      .map((r) => {
        if (r && r.section != null) return { section: String(r.section) };
        return {
          room:    String(r.room    || ''),
          video:   String(r.video   || ''),
          audio:   String(r.audio   || ''),
          sources: String(r.sources || ''),
          // Preserve the extra buckets the parser now produces so the
          // renderer + salesperson editor can surface them. Rooms with only
          // network or phone content still deserve a call-out row — e.g.
          // 217 Cedar Closet / Safe Room (network provision) or the top-
          // level Phone and Intercom section. Previously we dropped these.
          network: String(r.network || ''),
          phone:   String(r.phone   || ''),
          scope:   String(r.scope   || ''),
          // Additional Options callout (Tom Clancy 2026-07-13): every
          // option on the proposal has to surface on the deck. Carry
          // the summary string through so the renderer emits an
          // "Option: Prepared for ..." row.
          add_alt_note: String(r.add_alt_note || ''),
        };
      })
      // Keep any row that has ANY meaningful content, plus section headers.
      // We do NOT drop rooms that lack video/audio/sources anymore — an
      // audio-zone-only room, a network-only room, an add-alt-only room,
      // and even a scope-only callout all belong in the client-facing table.
      .filter((r) => r.section != null
        || r.video || r.audio || r.sources
        || r.network || r.phone || r.scope
        || r.add_alt_note
        || r.room);
  }
  if (disciplineId === 'shades') {
    return rooms.map((r) => ({
      room:     String(r.room || ''),
      solar:    Number(r.solar)    || 0,
      blackout: Number(r.blackout) || 0,
      // Preserve Additional Options / recommend-add content so the
      // renderer can surface it as a call-out. Rooms whose ONLY scope
      // is in Additional Options (e.g. Stair Hall pre-wired for shades)
      // deserve a row on the client-facing table.
      add_alt_solar:         Number(r.add_alt_solar)         || 0,
      add_alt_blackout:      Number(r.add_alt_blackout)      || 0,
      add_alt_side_channels: Number(r.add_alt_side_channels) || 0,
      note: String(r.note || ''),
    })).filter((r) => r.room);
  }
  return [];
}

function sumSubtotals(disciplines) {
  return (disciplines || []).reduce((acc, d) => acc + (Number(d.subtotal) || 0), 0);
}

// Parse an input string like "$438,067" or "438067.5" into a Number.
function parseCurrencyInput(str) {
  if (str == null) return 0;
  const cleaned = String(str).replace(/[^0-9.-]/g, '');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : 0;
}

// Returns today's date as YYYYMMDD (for download filenames).
function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

// Strip internal-only flags from the project before sending to the generate
// endpoint. Preserves everything else (hero, disciplines with image refs, etc.).
function buildPayload(project) {
  const { grand_total_manual, ...rest } = project;
  return rest;
}

// -------------------------------------------------------------
// Step 1 — Upload proposals
// -------------------------------------------------------------
function Step1Upload({ parsedFiles, setParsedFiles, uploading, setUploading, uploadError, setUploadError, onAutoPopulate }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    if (files.length === 0) {
      setUploadError('Please upload PDF files.');
      return;
    }
    if (files.length > 8) {
      setUploadError('Up to 8 PDFs per batch. Upload the rest in additional batches — they will be added to the list.');
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('proposals', f);
      const { data } = await api.post('/exec-summary/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const items = (data && data.files) || [];
      // Merge with existing parsed files instead of replacing. If the user
      // re-uploads a PDF with the same original filename, the newer parse
      // result wins (keeps the list de-duplicated). Uploading files one at a
      // time now adds to the list — it does not overwrite the previous batch.
      setParsedFiles((prev) => {
        const byName = new Map();
        for (const f of prev || []) byName.set(f.original_name, f);
        for (const f of items) byName.set(f.original_name, f);
        return Array.from(byName.values());
      });
      onAutoPopulate(items);
    } catch (e) {
      setUploadError(e?.response?.data?.error || e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }, [setParsedFiles, setUploadError, setUploading, onAutoPopulate]);

  const onDrop = (ev) => {
    ev.preventDefault();
    setDragOver(false);
    handleFiles(ev.dataTransfer.files);
  };

  const okCount = parsedFiles.filter((f) => f.ok !== false).length;

  return (
    <Box>
      <Paper
        elevation={0}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current && inputRef.current.click()}
        sx={{
          p: 5, textAlign: 'center', cursor: 'pointer',
          border: '2px dashed',
          borderColor: dragOver ? NAVY : 'divider',
          bgcolor: dragOver ? 'rgba(17,68,122,0.04)' : 'transparent',
          transition: 'all 120ms ease',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
        <UploadIcon sx={{ fontSize: 40, color: NAVY, mb: 1 }} />
        <Typography variant="h6" sx={{ color: NAVY, fontWeight: 500 }}>
          Drop D-Tools client proposal PDFs here
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Or click to browse. Upload individually or in batches — files are added to the list, not replaced. Up to 8 per batch.
        </Typography>
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={22} sx={{ color: NAVY }} />
          </Box>
        )}
      </Paper>

      {uploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>
      )}

      {parsedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}
          >
            Parsed proposals ({okCount}/{parsedFiles.length})
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {parsedFiles.map((f, i) => (
              <ParsedProposalCard key={`${f.original_name}-${i}`} file={f} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// -------------------------------------------------------------
// Parser telemetry: each parsed PDF gets its own accordion so Dawn/Silvia
// can eyeball what the parser actually saw before advancing to Step 2.
// If a room came back with zero products, or the room count looks wrong,
// they catch it here — not on the finished deck.
// -------------------------------------------------------------
// Which buckets are meaningful for a given discipline. AV/IT rooms are
// bucketed by content type; shades rooms are bucketed by fabric type.
// Lighting has no per-room breakdown — the telemetry panel just shows
// aggregate counts.
function bucketsFor(disciplineId, room) {
  const bs = [];
  if (disciplineId === 'shades') {
    if (room.solar)          bs.push(`solar (${room.solar})`);
    if (room.blackout)       bs.push(`blackout (${room.blackout})`);
    if (room.side_channels)  bs.push(`side channels (${room.side_channels})`);
    // Additional Options / recommend-add: shades D-Tools flags as pre-wire
    // suggestions or budget alternates. Show them so the salesperson knows
    // the room has real scope even without base items.
    if (room.add_alt_solar)         bs.push(`+${room.add_alt_solar} solar (add-alt)`);
    if (room.add_alt_blackout)      bs.push(`+${room.add_alt_blackout} blackout (add-alt)`);
    if (room.add_alt_side_channels) bs.push(`+${room.add_alt_side_channels} side ch. (add-alt)`);
    return bs;
  }
  // Default: AV/IT taxonomy.
  if (room.video)   bs.push('video');
  if (room.audio)   bs.push('audio');
  if (room.sources) bs.push('sources');
  if (room.control) bs.push('control');
  if (room.network) bs.push('network');
  if (room.phone)   bs.push('phone');
  return bs;
}

function roomHasContent(disciplineId, room) {
  if (disciplineId === 'shades') {
    return (room.solar || 0) > 0
        || (room.blackout || 0) > 0
        || (room.side_channels || 0) > 0
        || (room.add_alt_solar || 0) > 0
        || (room.add_alt_blackout || 0) > 0
        || (room.add_alt_side_channels || 0) > 0;
  }
  return !!(room.video || room.audio || room.sources
            || room.network || room.phone || room.control);
}

function ParsedProposalCard({ file }) {
  const [expanded, setExpanded] = useState(false);
  const ok = file.ok !== false;
  const parsed = file.parsed || {};
  const rooms = Array.isArray(parsed.rooms) ? parsed.rooms : [];
  const disciplineId = file.discipline;
  const zeroProductRooms = rooms.filter((r) => !r.section && !roomHasContent(disciplineId, r));
  const withContentRooms = rooms.filter((r) => !r.section && roomHasContent(disciplineId, r));
  const totalRooms = rooms.filter((r) => !r.section).length;

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <PdfIcon sx={{ color: ok ? NAVY : 'error.main' }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {file.original_name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            {ok ? (
              <>
                <Chip size="small"
                  label={file.discipline === 'unknown' || !file.discipline
                    ? 'Discipline not recognized — won’t be added'
                    : file.discipline}
                  color={file.discipline === 'unknown' || !file.discipline ? 'warning' : 'default'}
                  sx={file.discipline && file.discipline !== 'unknown'
                    ? { bgcolor: 'rgba(17,68,122,0.08)', color: NAVY, fontWeight: 500 }
                    : { fontWeight: 500 }} />
                {file.grand_total ? (
                  <Chip size="small" label={formatCurrency(file.grand_total)}
                    sx={{ bgcolor: 'rgba(241,168,108,0.15)', color: '#8a4d1e', fontWeight: 500 }} />
                ) : null}
                {file.proposal_number ? (
                  <Chip size="small" variant="outlined" label={file.proposal_number} />
                ) : null}
                {totalRooms > 0 ? (
                  <Chip size="small" variant="outlined"
                    label={`${withContentRooms.length}/${totalRooms} rooms with content`}
                    color={zeroProductRooms.length > totalRooms / 3 ? 'warning' : 'default'} />
                ) : null}
                {file.llm_review && file.llm_review.ok ? (
                  <Chip size="small" variant="outlined"
                    label={`AI-reviewed · ${file.llm_review.confidence || 'reviewed'}${
                      file.llm_review.issues && file.llm_review.issues.length
                        ? ` · ${file.llm_review.issues.length} note${file.llm_review.issues.length === 1 ? '' : 's'}`
                        : ''
                    }`}
                    color={file.llm_review.confidence === 'low' ? 'warning' : 'success'} />
                ) : file.llm_review && !file.llm_review.ok ? (
                  <Chip size="small" variant="outlined"
                    label="AI review skipped"
                    color="default" />
                ) : null}
              </>
            ) : (
              <Chip size="small" color="error" icon={<ErrIcon />} label="Parse failed" />
            )}
          </Stack>
          {!ok && file.error && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {String(file.error).slice(0, 240)}
            </Typography>
          )}
        </Box>
        {ok && (
          <>
            {rooms.length > 0 && (
              <Button
                size="small"
                onClick={() => setExpanded((e) => !e)}
                sx={{ textTransform: 'none', color: NAVY }}
              >
                {expanded ? 'Hide details' : 'What did the parser see?'}
              </Button>
            )}
            <OkIcon sx={{ color: 'success.main' }} />
          </>
        )}
      </Box>

      {expanded && ok && (
        <Box sx={{ px: 2, pb: 2, bgcolor: 'rgba(17,68,122,0.03)', borderTop: '1px solid', borderColor: 'divider' }}>
          {file.llm_review && file.llm_review.ok && Array.isArray(file.llm_review.issues) && file.llm_review.issues.length > 0 && (
            <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Notes from the AI review of this proposal
              </Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                {file.llm_review.issues.map((iss, i) => (
                  <li key={i}>
                    <Typography variant="caption" color="text.secondary">{iss}</Typography>
                  </li>
                ))}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Confidence: <b>{file.llm_review.confidence || 'medium'}</b>. These are
                things the AI flagged for the salesperson to double-check before
                sending the deck — they do not block generation.
              </Typography>
            </Alert>
          )}
          {zeroProductRooms.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {zeroProductRooms.length} room{zeroProductRooms.length === 1 ? '' : 's'} came back with zero products.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {zeroProductRooms.map((r) => r.room).filter(Boolean).join(', ')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                If these rooms actually have scope in the proposal, the parser missed something.
                Continue and I&rsquo;ll flag it, or reach out and I&rsquo;ll fix the parser.
              </Typography>
            </Alert>
          )}
          <Table size="small" sx={{ mt: 1, bgcolor: 'transparent' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary' }}>
                  Room
                </TableCell>
                <TableCell sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary' }} align="right">
                  Products
                </TableCell>
                <TableCell sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary' }}>
                  Buckets found
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.filter((r) => !r.section).map((r, idx) => {
                const buckets = bucketsFor(disciplineId, r);
                const isEmpty = !roomHasContent(disciplineId, r);
                return (
                  <TableRow key={idx}
                    sx={{ bgcolor: isEmpty ? 'rgba(237,108,2,0.08)' : 'transparent' }}>
                    <TableCell sx={{ fontSize: 12 }}>{r.room || <i>(unnamed)</i>}</TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">{r.product_count || 0}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {buckets.length ? buckets.join(' · ') : <span style={{ color: '#ed6c02' }}>none</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

// -------------------------------------------------------------
// Discipline detail editor — per-discipline narrative fields
// (experience lede, stats, system items, room-by-room, network block,
// summary strip). Renders inside each discipline card in Step 2.
// -------------------------------------------------------------

// Which disciplines get a room-by-room page in the rendered deck.
// Doctrine (locked 2026-06-30, confirmed 2026-07-04):
//   - AV/IT: room-by-room + Network & Infrastructure + summary strip.
//   - Motorized Window Treatments: room-by-room (solar/blackout counts).
//   - Lighting Control: NO room-by-room page. One page only.
//   - Lighting Fixtures: overview only, no equipment image.
function hasRoomsPage(discId) {
  return discId === 'avit' || discId === 'shades';
}
function hasNetworkBlock(discId) {
  return discId === 'avit';
}
function hasSummaryStrip(discId) {
  return discId === 'avit' || discId === 'shades';
}

function StatsEditor({ stats, onChange, helper }) {
  const rows = stats || [];
  const patchRow = (i, partial) => {
    const next = rows.map((r, j) => (i === j ? { ...r, ...partial } : r));
    onChange(next);
  };
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));
  const addRow = () => onChange([...rows, { num: '', lab: '' }]);
  return (
    <Box>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {helper}
        </Typography>
      )}
      <Stack spacing={1}>
        {rows.map((r, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Number / value"
              value={r.num || ''}
              onChange={(e) => patchRow(i, { num: e.target.value })}
              sx={{ width: 140 }}
              placeholder="e.g. 4 or Whole"
            />
            <TextField
              size="small"
              label="Label"
              value={r.lab || ''}
              onChange={(e) => patchRow(i, { lab: e.target.value })}
              fullWidth
              placeholder="e.g. Premium Displays \u2014 OLED"
            />
            <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addRow}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', color: NAVY }}
        >
          Add stat
        </Button>
      </Stack>
    </Box>
  );
}

function BulletsEditor({ items, onChange, placeholder }) {
  const rows = items || [];
  const patchRow = (i, v) => onChange(rows.map((r, j) => (i === j ? v : r)));
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));
  const addRow = () => onChange([...rows, '']);
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    const t = next[i]; next[i] = next[j]; next[j] = t;
    onChange(next);
  };
  return (
    <Stack spacing={1}>
      {rows.map((r, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            value={r}
            onChange={(e) => patchRow(i, e.target.value)}
            fullWidth
            placeholder={placeholder}
          />
          <IconButton size="small" disabled={i === 0} onClick={() => moveRow(i, -1)}>
            <UpIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" disabled={i === rows.length - 1} onClick={() => moveRow(i, +1)}>
            <DownIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', color: NAVY }}
      >
        Add bullet
      </Button>
    </Stack>
  );
}

function AvRoomsEditor({ rooms, onChange }) {
  const rows = rooms || [];
  const patchRow = (i, partial) => onChange(rows.map((r, j) => (i === j ? { ...r, ...partial } : r)));
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));
  const addRoom = () => onChange([...rows, { room: '', video: '', audio: '', sources: '' }]);
  const addSection = () => onChange([...rows, { section: 'Section header' }]);
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    const t = next[i]; next[i] = next[j]; next[j] = t;
    onChange(next);
  };
  return (
    <Stack spacing={1}>
      {rows.map((r, i) => (
        r.section != null ? (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Section header"
              value={r.section || ''}
              onChange={(e) => patchRow(i, { section: e.target.value })}
              fullWidth
              sx={{ '& .MuiInputBase-input': { fontWeight: 500, color: NAVY, letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 12 } }}
            />
            <IconButton size="small" disabled={i === 0} onClick={() => moveRow(i, -1)}>
              <UpIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={i === rows.length - 1} onClick={() => moveRow(i, +1)}>
              <DownIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <Grid container spacing={1} key={i} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField size="small" label="Room" fullWidth
                value={r.room || ''} onChange={(e) => patchRow(i, { room: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField size="small" label="Video" fullWidth
                value={r.video || ''} onChange={(e) => patchRow(i, { video: e.target.value })}
                placeholder='e.g. 83″ LG OLED Evo G5' />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField size="small" label="Audio" fullWidth
                value={r.audio || ''} onChange={(e) => patchRow(i, { audio: e.target.value })}
                placeholder='e.g. Sonos Arc Ultra, 6× James in-ceiling' />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField size="small" label="Sources" fullWidth
                value={r.sources || ''} onChange={(e) => patchRow(i, { sources: e.target.value })}
                placeholder='e.g. Apple TV 4K, Cable' />
            </Grid>
            <Grid item xs={12} md={1}>
              <Stack direction="row" justifyContent="flex-end">
                <IconButton size="small" disabled={i === 0} onClick={() => moveRow(i, -1)}>
                  <UpIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={i === rows.length - 1} onClick={() => moveRow(i, +1)}>
                  <DownIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: 'error.main' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        )
      ))}
      <Stack direction="row" spacing={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={addRoom}
          sx={{ textTransform: 'none', color: NAVY }}>Add room</Button>
        <Button size="small" startIcon={<AddIcon />} onClick={addSection}
          sx={{ textTransform: 'none', color: NAVY }}>Add section header</Button>
      </Stack>
    </Stack>
  );
}

function ShadeRoomsEditor({ rooms, onChange }) {
  const rows = rooms || [];
  const patchRow = (i, partial) => onChange(rows.map((r, j) => (i === j ? { ...r, ...partial } : r)));
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));
  const addRoom = () => onChange([...rows, { room: '', solar: 0, blackout: 0 }]);
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    const t = next[i]; next[i] = next[j]; next[j] = t;
    onChange(next);
  };
  const totSolar = rows.reduce((a, r) => a + (Number(r.solar) || 0), 0);
  const totBlack = rows.reduce((a, r) => a + (Number(r.blackout) || 0), 0);
  return (
    <Stack spacing={1}>
      {rows.map((r, i) => (
        <Grid container spacing={1} key={i} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField size="small" label="Room" fullWidth
              value={r.room || ''} onChange={(e) => patchRow(i, { room: e.target.value })} />
          </Grid>
          <Grid item xs={5} md={3}>
            <TextField size="small" label="Solar shades" type="number" fullWidth
              inputProps={{ min: 0 }}
              value={r.solar ?? 0}
              onChange={(e) => patchRow(i, { solar: parseInt(e.target.value, 10) || 0 })} />
          </Grid>
          <Grid item xs={5} md={3}>
            <TextField size="small" label="Blackout shades" type="number" fullWidth
              inputProps={{ min: 0 }}
              value={r.blackout ?? 0}
              onChange={(e) => patchRow(i, { blackout: parseInt(e.target.value, 10) || 0 })} />
          </Grid>
          <Grid item xs={2} md={1}>
            <Stack direction="row" justifyContent="flex-end">
              <IconButton size="small" disabled={i === 0} onClick={() => moveRow(i, -1)}>
                <UpIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={i === rows.length - 1} onClick={() => moveRow(i, +1)}>
                <DownIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: 'error.main' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addRoom}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', color: NAVY }}>Add room</Button>
      {rows.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Totals: {totSolar} solar · {totBlack} blackout · {rows.length} rooms
        </Typography>
      )}
    </Stack>
  );
}

function NetworkBlockEditor({ block, onChange }) {
  const value = block || { label: 'Network & Infrastructure', title: '', items: [] };
  const items = value.items || [];
  const patchTop = (partial) => onChange({ ...value, ...partial });
  const patchItem = (i, partial) => {
    const next = items.map((it, j) => (i === j ? { ...it, ...partial } : it));
    onChange({ ...value, items: next });
  };
  const removeItem = (i) => onChange({ ...value, items: items.filter((_, j) => j !== i) });
  const addItem = () => onChange({ ...value, items: [...items, { head: '', body: '' }] });
  return (
    <Stack spacing={1.5}>
      <Grid container spacing={1}>
        <Grid item xs={12} md={4}>
          <TextField size="small" label="Block subtitle" fullWidth
            helperText='Small line above the block title'
            value={value.label || ''} onChange={(e) => patchTop({ label: e.target.value })} />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField size="small" label="Block title (italic sub-line)" fullWidth
            value={value.title || ''} onChange={(e) => patchTop({ title: e.target.value })} />
        </Grid>
      </Grid>
      <Divider light />
      {items.map((it, i) => (
        <Grid container spacing={1} key={i} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <TextField size="small" label="Heading" fullWidth
              value={it.head || ''} onChange={(e) => patchItem(i, { head: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField size="small" label="Body" fullWidth multiline minRows={1}
              value={it.body || ''} onChange={(e) => patchItem(i, { body: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton size="small" onClick={() => removeItem(i)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addItem}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', color: NAVY }}>Add block item</Button>
    </Stack>
  );
}

function DisciplineDetailEditor({ discipline, onPatch }) {
  const d = discipline;
  return (
    <Box sx={{ mt: 2 }}>
      <Accordion disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
            Narrative & content
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2.5}>
            {/* --- Experience lede + image caption ---------------- */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Experience paragraph (“The Experience” body on the overview page)
              </Typography>
              <TextField
                fullWidth multiline minRows={3}
                value={d.experience_lede || ''}
                onChange={(e) => onPatch({ experience_lede: e.target.value })}
                placeholder="One paragraph of luxury-register copy describing how the system feels in daily use."
              />
            </Box>
            {!d.no_equipment && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small" fullWidth
                    label="Image caption"
                    value={d.image_caption || ''}
                    onChange={(e) => onPatch({ image_caption: e.target.value })}
                    helperText="Small caption baked into the hero image on the discipline page."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small" fullWidth
                    label="Proposal reference (D-Tools #)"
                    value={d.proposal_ref || ''}
                    onChange={(e) => onPatch({ proposal_ref: e.target.value })}
                    placeholder="e.g. 26-0016"
                    helperText="Prints in the page footer."
                  />
                </Grid>
              </Grid>
            )}

            {/* --- Stats grid ------------------------------------- */}
            <Box>
              <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
                Stat boxes (on overview page)
              </Typography>
              <StatsEditor
                stats={d.stats}
                onChange={(next) => onPatch({ stats: next })}
                helper="3 stats recommended. Values can be numeric (e.g. 41) or shorthand (e.g. Whole)."
              />
            </Box>

            {/* --- System bullets --------------------------------- */}
            <Box>
              <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
                System bullets (“The System” list)
              </Typography>
              <BulletsEditor
                items={d.system_items}
                onChange={(next) => onPatch({ system_items: next })}
                placeholder="One product/scope statement per line"
              />
            </Box>

            {/* --- Room-by-room (avit + shades only) --------------- */}
            {hasRoomsPage(d.id) && (
              <Box>
                <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
                  Room-by-room page
                </Typography>
                <TextField
                  size="small" fullWidth
                  label="Room-by-room intro line"
                  value={d.rooms_intro || ''}
                  onChange={(e) => onPatch({ rooms_intro: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                {d.id === 'avit'
                  ? <AvRoomsEditor rooms={d.rooms} onChange={(next) => onPatch({ rooms: next })} />
                  : <ShadeRoomsEditor rooms={d.rooms} onChange={(next) => onPatch({ rooms: next })} />}
                <TextField
                  size="small" fullWidth multiline minRows={1}
                  sx={{ mt: 1.5 }}
                  label="Room table footnote (HTML permitted: <br/>)"
                  value={d.rooms_footnote || ''}
                  onChange={(e) => onPatch({ rooms_footnote: e.target.value })}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Leave rooms empty to skip the room-by-room page and keep the discipline to a single page.
                </Typography>
              </Box>
            )}

            {/* --- Network block (avit only) ---------------------- */}
            {hasNetworkBlock(d.id) && (
              <Box>
                <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
                  Network & Infrastructure block
                </Typography>
                <NetworkBlockEditor
                  block={d.network_block}
                  onChange={(next) => onPatch({ network_block: next })}
                />
              </Box>
            )}

            {/* --- Summary strip (avit + shades) ------------------ */}
            {hasSummaryStrip(d.id) && (
              <Box>
                <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
                  Summary strip (bottom of room-by-room page)
                </Typography>
                <StatsEditor
                  stats={d.summary_strip}
                  onChange={(next) => onPatch({ summary_strip: next })}
                  helper="4 items recommended, high-level numeric callouts."
                />
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

// -------------------------------------------------------------
// Step 2 — Header + disciplines editor
// -------------------------------------------------------------
function Step2Header({ project, setProject }) {
  const disciplines = project.disciplines || [];
  const gtAuto = sumSubtotals(disciplines);
  const gtValue = project.grand_total_manual ? project.grand_total : gtAuto;

  const patchProject = (partial) => setProject((p) => ({ ...p, ...partial }));

  const patchDiscipline = (idx, partial) => {
    setProject((p) => {
      const next = [...(p.disciplines || [])];
      next[idx] = { ...next[idx], ...partial };
      return {
        ...p,
        disciplines: next,
        grand_total: p.grand_total_manual ? p.grand_total : sumSubtotals(next),
      };
    });
  };

  const addDiscipline = (id) => {
    const template = DISCIPLINE_TEMPLATES[id];
    const already = new Set(disciplines.map((d) => d.id));
    const finalId =
      template && !already.has(template.id)
        ? template.id
        : `custom-${disciplines.length + 1}`;
    const row = template && !already.has(template.id)
      ? { ...template }
      : { id: finalId, eyebrow: '', title: '', tag: '', subtotal: 0 };
    setProject((p) => {
      const next = [...(p.disciplines || []), row];
      return {
        ...p,
        disciplines: next,
        grand_total: p.grand_total_manual ? p.grand_total : sumSubtotals(next),
      };
    });
  };

  const removeDiscipline = (idx) => {
    setProject((p) => {
      const next = (p.disciplines || []).filter((_, i) => i !== idx);
      return {
        ...p,
        disciplines: next,
        grand_total: p.grand_total_manual ? p.grand_total : sumSubtotals(next),
      };
    });
  };

  const moveDiscipline = (idx, dir) => {
    setProject((p) => {
      const arr = [...(p.disciplines || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return p;
      const t = arr[idx];
      arr[idx] = arr[j];
      arr[j] = t;
      return { ...p, disciplines: arr };
    });
  };

  const usedIds = new Set(disciplines.map((d) => d.id));
  const addableTemplates = Object.values(DISCIPLINE_TEMPLATES).filter((t) => !usedIds.has(t.id));

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}
      >
        Cover page
      </Typography>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client"
            fullWidth
            required
            value={project.client}
            onChange={(e) => patchProject({ client: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Cover intro line"
            fullWidth
            value={project.cover_eyebrow}
            onChange={(e) => patchProject({ cover_eyebrow: e.target.value })}
            helperText='Small line above the client name on the cover — e.g. "A technology solution presented for"'
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Address line 1"
            fullWidth
            required
            value={project.address_line1}
            onChange={(e) => patchProject({ address_line1: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Address line 2"
            fullWidth
            value={project.address_line2}
            onChange={(e) => patchProject({ address_line2: e.target.value })}
            helperText='e.g. "New York, NY 10065"'
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
        <Typography
          variant="overline"
          sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500, flexGrow: 1 }}
        >
          Disciplines ({disciplines.length})
        </Typography>

        {addableTemplates.length > 0 && (
          <Select
            value=""
            displayEmpty
            size="small"
            renderValue={() => 'Add discipline…'}
            onChange={(e) => addDiscipline(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {addableTemplates.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
            ))}
            <MenuItem value={`custom-${Date.now()}`}>Custom…</MenuItem>
          </Select>
        )}
      </Stack>

      <Stack spacing={2}>
        {disciplines.length === 0 && (
          <Alert severity="info">
            No disciplines yet. Upload proposals in Step 1 or add one manually above.
          </Alert>
        )}

        {disciplines.map((d, idx) => (
          <Paper key={`${d.id}-${idx}`} variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Title"
                  fullWidth
                  size="small"
                  value={d.title || ''}
                  onChange={(e) => patchDiscipline(idx, { title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Subtitle"
                  fullWidth
                  size="small"
                  value={d.eyebrow || ''}
                  onChange={(e) => patchDiscipline(idx, { eyebrow: e.target.value })}
                  helperText='Small line above the title — e.g. "Audio, Video & Networking"'
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Tag line"
                  fullWidth
                  size="small"
                  value={d.tag || ''}
                  onChange={(e) => patchDiscipline(idx, { tag: e.target.value })}
                  helperText='One-line theme under the title — e.g. "Designed and engineered as a single instrument"'
                />
              </Grid>
              <Grid item xs={8} md={2}>
                <TextField
                  label="Subtotal"
                  fullWidth
                  size="small"
                  value={d.subtotal ? formatCurrency(d.subtotal) : ''}
                  onChange={(e) =>
                    patchDiscipline(idx, { subtotal: parseCurrencyInput(e.target.value) })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  placeholder="0"
                />
              </Grid>
              <Grid item xs={4} md={1}>
                <Stack direction="row" justifyContent="flex-end">
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        disabled={idx === 0}
                        onClick={() => moveDiscipline(idx, -1)}
                      >
                        <UpIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={idx === disciplines.length - 1}
                        onClick={() => moveDiscipline(idx, +1)}
                      >
                        <DownIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => removeDiscipline(idx)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
            <DisciplineDetailEditor
              discipline={d}
              onPatch={(partial) => patchDiscipline(idx, partial)}
            />
          </Paper>
        ))}

        {addableTemplates.length === 0 && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => addDiscipline('custom')}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', color: NAVY, borderColor: NAVY }}
          >
            Add custom discipline
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
            Grand total
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {project.grand_total_manual
              ? 'Manually set — auto-sum disabled.'
              : `Auto-sum of subtotals · ${formatCurrency(gtAuto)}`}
          </Typography>
        </Box>
        <TextField
          size="small"
          value={gtValue ? formatCurrency(gtValue) : ''}
          onChange={(e) =>
            setProject((p) => ({
              ...p,
              grand_total_manual: true,
              grand_total: parseCurrencyInput(e.target.value),
            }))
          }
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{ minWidth: 200 }}
        />
        {project.grand_total_manual && (
          <Button
            size="small"
            onClick={() =>
              setProject((p) => ({
                ...p,
                grand_total_manual: false,
                grand_total: sumSubtotals(p.disciplines),
              }))
            }
            sx={{ textTransform: 'none', color: NAVY }}
          >
            Reset to auto
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: 3 }} />

      {/* Salesperson / contact card (renders as "Next Step" contact block
          on the final page of the deck). */}
      <Typography
        variant="overline"
        sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}
      >
        Salesperson (Next Step contact card)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Appears on the closing page of the executive summary. All four fields ship on the deck.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Salesperson name"
            fullWidth
            required
            value={(project.salesperson && project.salesperson.name) || ''}
            onChange={(e) =>
              setProject((p) => ({
                ...p,
                salesperson: { ...(p.salesperson || {}), name: e.target.value },
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Title"
            fullWidth
            value={(project.salesperson && project.salesperson.title) || ''}
            onChange={(e) =>
              setProject((p) => ({
                ...p,
                salesperson: { ...(p.salesperson || {}), title: e.target.value },
              }))
            }
            placeholder="e.g. Senior Sales Consultant"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Email"
            fullWidth
            required
            type="email"
            value={(project.salesperson && project.salesperson.email) || ''}
            onChange={(e) =>
              setProject((p) => ({
                ...p,
                salesperson: { ...(p.salesperson || {}), email: e.target.value },
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Phone"
            fullWidth
            value={(project.salesperson && project.salesperson.phone) || ''}
            onChange={(e) =>
              setProject((p) => ({
                ...p,
                salesperson: { ...(p.salesperson || {}), phone: e.target.value },
              }))
            }
            placeholder="e.g. (516) 555-0142"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// -------------------------------------------------------------
// Shared: a single library-image card (clickable, selectable)
// -------------------------------------------------------------
function LibraryCard({ item, selected, onSelect }) {
  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        height: '100%',
        borderColor: selected ? NAVY : 'divider',
        borderWidth: selected ? 2 : 1,
      }}
    >
      <CardActionArea onClick={onSelect} sx={{ height: '100%' }}>
        {selected && (
          <OkIcon
            sx={{
              position: 'absolute', top: 6, right: 6, zIndex: 1,
              color: NAVY, bgcolor: 'background.paper', borderRadius: '50%',
            }}
          />
        )}
        <Box
          component="img"
          src={item.file}
          alt={item.title}
          sx={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
        />
        <CardContent sx={{ p: 1.5 }}>
          <Typography sx={{ color: NAVY, fontSize: 14, fontWeight: 500 }}>
            {item.title}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            {(item.tags || []).slice().sort((a, b) => String(a).localeCompare(String(b))).map((t) => (
              <Chip key={t} size="small" label={t} sx={{ bgcolor: 'rgba(17,68,122,0.08)', color: NAVY }} />
            ))}
          </Stack>
          {item.best_for && (
            <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
              {item.best_for}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// -------------------------------------------------------------
// Shared: library / upload picker used by hero + each discipline.
// `pool` is the sorted list of library items; `selectedFile` is the
// currently-selected library file path (for highlighting).
// -------------------------------------------------------------
// pool: array of library items (hero or hardware).
// disciplineId (optional): when set, the picker starts with a "Recommended"
//   subset that matches this discipline; the salesperson can toggle to "All"
//   to see every image.
// searchTerm (optional): free-text filter over title + tags. Falsy = no filter.
function filterPool(pool, disciplineId, showAll, searchTerm) {
  if (!Array.isArray(pool)) return [];
  const q = String(searchTerm || '').trim().toLowerCase();
  const matchesSearch = (item) => {
    if (!q) return true;
    const hay = [
      item.title || '',
      (item.tags || []).join(' '),
      (item.disciplines || []).join(' '),
      item.source || '',
    ].join(' ').toLowerCase();
    return hay.includes(q);
  };
  if (showAll || !disciplineId) return pool.filter(matchesSearch);
  // Match on canonical id + a few aliases the manifest uses interchangeably.
  const aliases = new Set([disciplineId]);
  if (disciplineId === 'lighting') aliases.add('lighting-control');
  if (disciplineId === 'lighting-control') aliases.add('lighting');
  const recommended = pool.filter((item) => {
    const disc = item.disciplines || [];
    return disc.some((d) => aliases.has(d));
  });
  const filtered = recommended.filter(matchesSearch);
  // If the filter emptied the list, fall back gracefully to the full pool
  // so the salesperson never sees an empty picker.
  if (filtered.length === 0 && !q) return pool.filter(matchesSearch);
  return filtered;
}

function ImageSourcePicker({ pool, selectedFile, onSelectLibrary, onUpload, disciplineId }) {
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef(null);

  const filteredPool = useMemo(
    () => filterPool(pool, disciplineId, showAll, searchTerm),
    [pool, disciplineId, showAll, searchTerm],
  );
  const recommendedCount = useMemo(
    () => filterPool(pool, disciplineId, false, '').length,
    [pool, disciplineId],
  );

  // Revoke the object URL when the component unmounts / URL changes.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleFile = async (file) => {
    if (!file) return;
    setErr(null);
    setBusy(true);
    const localUrl = URL.createObjectURL(file);
    try {
      const fd = new FormData();
      fd.append('hero', file);
      const { data } = await api.post('/exec-summary/hero', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return localUrl; });
      onUpload(data);
    } catch (e) {
      URL.revokeObjectURL(localUrl);
      setErr(e?.response?.data?.error || e?.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' }, '& .Mui-selected': { color: NAVY }, '& .MuiTabs-indicator': { bgcolor: NAVY } }}
      >
        <Tab label="Choose from library" />
        <Tab label="Upload your own" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {disciplineId && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={showAll ? 'all' : 'recommended'}
                onChange={(_, v) => { if (v) setShowAll(v === 'all'); }}
              >
                <ToggleButton value="recommended" sx={{ textTransform: 'none' }}>
                  Recommended ({recommendedCount})
                </ToggleButton>
                <ToggleButton value="all" sx={{ textTransform: 'none' }}>
                  All images ({pool.length})
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                placeholder="Filter by tag or title (e.g. palladiom, drapery, theater)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
            </Stack>
          )}
          {!disciplineId && (
            <TextField
              size="small" fullWidth sx={{ mb: 2 }}
              placeholder="Filter by tag or title (e.g. modern, dusk, aerial)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}
          {filteredPool.length === 0 ? (
            <Alert severity="info">No images match. Clear the filter or upload your own.</Alert>
          ) : (
            <Grid container spacing={2}>
              {filteredPool.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <LibraryCard
                    item={item}
                    selected={selectedFile === item.file}
                    onSelect={() => onSelectLibrary(item)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleFile(e.target.files && e.target.files[0])}
          />
          <Button
            variant="outlined"
            startIcon={busy ? <CircularProgress size={16} sx={{ color: NAVY }} /> : <UploadIcon />}
            disabled={busy}
            onClick={() => inputRef.current && inputRef.current.click()}
            sx={{ textTransform: 'none', color: NAVY, borderColor: NAVY }}
          >
            {busy ? 'Uploading…' : 'Choose image'}
          </Button>
          {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt="Uploaded preview"
              sx={{ mt: 2, maxWidth: 320, width: '100%', borderRadius: 1, display: 'block' }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

// -------------------------------------------------------------
// Step 3 — Imagery picker
// -------------------------------------------------------------
function Step3Imagery({ project, setProject, missingImagery }) {
  const [library, setLibrary] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Fetch once per Step-3 entry (parent unmounts us when activeStep !== 2).
  useEffect(() => {
    let cancelled = false;
    api.get('/exec-summary/library')
      .then(({ data }) => { if (!cancelled) setLibrary(data || { heroes: [], hardware: [] }); })
      .catch((e) => { if (!cancelled) setLoadError(e?.response?.data?.error || e?.message || 'Failed to load image library.'); });
    return () => { cancelled = true; };
  }, []);

  const heroes = useMemo(
    () => ((library && library.heroes) || []).slice().sort((a, b) => String(a.title).localeCompare(String(b.title))),
    [library],
  );
  const hardware = useMemo(
    () => ((library && library.hardware) || []).slice().sort((a, b) => String(a.title).localeCompare(String(b.title))),
    [library],
  );

  const setDisciplineImage = (idx, partial) => {
    setProject((p) => ({
      ...p,
      disciplines: p.disciplines.map((d, i) =>
        i === idx ? { ...d, image: { ...(d.image || {}), ...partial } } : d),
    }));
  };

  const heroFile = project.hero && project.hero.source === 'library' ? project.hero.file : null;

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }
  if (!library) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  const missing = missingImagery || { hero: !project.hero, disciplines: [] };
  const hasMissing = missing.hero || missing.disciplines.length > 0;

  return (
    <Box>
      {hasMissing && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Images required before you can continue.
          </Typography>
          <Typography variant="body2">
            {missing.hero && <>Cover hero image is not set. </>}
            {missing.disciplines.length > 0 && (
              <>Missing discipline image(s): <b>{missing.disciplines.join(', ')}</b>.</>
            )}
          </Typography>
        </Alert>
      )}
      {/* (A) Hero image */}
      <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
        Hero image (cover page){missing.hero && <span style={{ color: '#c62828' }}> — required</span>}
      </Typography>
      <Box sx={{ mt: 1, mb: 4 }}>
        <ImageSourcePicker
          pool={heroes}
          selectedFile={heroFile}
          onSelectLibrary={(item) =>
            setProject((p) => ({ ...p, hero: { source: 'library', file: item.file } }))}
          onUpload={(data) =>
            setProject((p) => ({ ...p, hero: { source: 'upload', stored_path: data.stored_path } }))}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* (B) Per-discipline images */}
      <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
        Discipline images (optional)
      </Typography>
      <Box sx={{ mt: 1 }}>
        {(project.disciplines || []).map((d, idx) => {
          const img = d.image || {};
          const selectedFile = img.source === 'library' ? img.file : null;
          return (
            <Accordion key={`${d.id}-${idx}`} variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                  <Typography sx={{ color: NAVY, fontWeight: 500 }}>
                    {d.title || `Discipline ${idx + 1}`}
                  </Typography>
                  {d.no_equipment ? (
                    <Chip size="small" label="No image needed" sx={{ bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary' }} />
                  ) : ((d.image || {}).source ? (
                    <Chip size="small" icon={<OkIcon fontSize="small" />} label="Image set" sx={{ bgcolor: 'rgba(46,125,50,0.12)', color: 'success.dark', '& .MuiChip-icon': { color: 'success.dark' } }} />
                  ) : (
                    <Chip size="small" color="warning" label="Image required" />
                  ))}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <ImageSourcePicker
                  pool={hardware}
                  disciplineId={d.id}
                  selectedFile={selectedFile}
                  onSelectLibrary={(item) => setDisciplineImage(idx, { source: 'library', file: item.file, stored_path: undefined })}
                  onUpload={(data) => setDisciplineImage(idx, { source: 'upload', stored_path: data.stored_path, file: undefined })}
                />
                <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Caption"
                      fullWidth
                      size="small"
                      value={img.caption || ''}
                      onChange={(e) => setDisciplineImage(idx, { caption: e.target.value.slice(0, 60) })}
                      helperText={`${(img.caption || '').length}/60`}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={img.position || 'left'}
                      onChange={(_, v) => { if (v) setDisciplineImage(idx, { position: v }); }}
                    >
                      <ToggleButton value="left" sx={{ textTransform: 'none' }}>Left</ToggleButton>
                      <ToggleButton value="right" sx={{ textTransform: 'none' }}>Right</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!img.brighten}
                          onChange={(e) => setDisciplineImage(idx, { brighten: e.target.checked })}
                          sx={{
                            '& .Mui-checked': { color: PEACH },
                            '& .Mui-checked + .MuiSwitch-track': { bgcolor: PEACH },
                          }}
                        />
                      }
                      label="Brighten photo (+5% exposure)"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
        {(project.disciplines || []).length === 0 && (
          <Alert severity="info">No disciplines to attach images to.</Alert>
        )}
      </Box>
    </Box>
  );
}

// -------------------------------------------------------------
// Step 4 — Review & generate
// -------------------------------------------------------------
function Step4Review({ project, drafts, setDrafts, parsedFiles }) {
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [generatedBlobUrl, setGeneratedBlobUrl] = useState(null);
  const [summaryId, setSummaryId] = useState(null);

  // Drafts table sort state.
  const [sortField, setSortField] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');

  const refreshDrafts = useCallback(() => {
    api.get('/exec-summary/list')
      .then(({ data }) => setDrafts((data && data.items) || []))
      .catch(() => { /* non-fatal: drafts panel just stays empty */ });
  }, [setDrafts]);

  // Fetch drafts on mount.
  useEffect(() => { refreshDrafts(); }, [refreshDrafts]);

  // Revoke the generated blob URL on unmount / when replaced.
  useEffect(() => () => { if (generatedBlobUrl) URL.revokeObjectURL(generatedBlobUrl); }, [generatedBlobUrl]);

  const handleGenerate = async () => {
    setGenError(null);
    setGenerating(true);
    try {
      // Preserve exactly what the parser saw + which source PDFs were used,
      // alongside the render payload. This is the debug archive: months from
      // now when a proposal parse looks off, we can pull the saved parse
      // results for the deck and diff against the current parser output.
      const parseResults = (parsedFiles || [])
        .filter((f) => f && f.ok !== false)
        .map((f) => ({
          original_name:   f.original_name,
          stored_path:     f.stored_path,
          size_bytes:      f.size_bytes,
          discipline:      f.discipline,
          proposal_number: f.proposal_number,
          grand_total:     f.grand_total,
          header:          f.header,
          parsed:          f.parsed,
          suggestions:     f.suggestions,
        }));
      const sourcePdfPaths = (parsedFiles || [])
        .filter((f) => f && f.stored_path)
        .map((f) => ({ original_name: f.original_name, stored_path: f.stored_path }));

      const response = await api.post(
        '/exec-summary/generate',
        {
          project: buildPayload(project),
          save: true,
          parse_results: parseResults.length ? parseResults : null,
          source_pdf_paths: sourcePdfPaths.length ? sourcePdfPaths : null,
        },
        { responseType: 'blob' },
      );
      const id = response.headers['x-exec-summary-id'] || response.headers['X-Exec-Summary-Id'];
      const url = URL.createObjectURL(response.data);
      setGeneratedBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      setSummaryId(id || null);
      refreshDrafts();
    } catch (e) {
      // Error body may be a Blob (responseType: blob); fall back to message.
      setGenError(e?.message || 'PDF generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Download a saved summary PDF. We can't just point <a href> at the
  // /pdf endpoint — that route requires a Bearer token, which the browser
  // won't attach for a plain navigation. Fetch through the api client
  // (auth interceptor adds the header), then open a blob URL.
  const handleDownloadDraft = async (id) => {
    try {
      const res = await api.get(`/exec-summary/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      let msg = e?.response?.data?.error || e?.message || 'Download failed.';
      const blob = e?.response?.data;
      if (blob && typeof blob.text === 'function') {
        try {
          const txt = await blob.text();
          try { msg = JSON.parse(txt).error || msg; } catch { msg = txt || msg; }
        } catch {}
      }
      setGenError(msg);
    }
  };

  const sortedDrafts = useMemo(() => {
    const rows = [...(drafts || [])];
    rows.sort((a, b) => {
      let cmp;
      if (sortField === 'grand_total') {
        cmp = (Number(a.grand_total) || 0) - (Number(b.grand_total) || 0);
      } else if (sortField === 'updated_at' || sortField === 'created_at') {
        cmp = String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
      } else {
        cmp = String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows; // full shared library, sorted
  }, [drafts, sortField, sortDir]);

  const COLUMNS = [
    { field: 'client', label: 'Client' },
    { field: 'project_name', label: 'Project' },
    { field: 'created_by_name', label: 'Created by' },
    { field: 'grand_total', label: 'Grand Total', align: 'right' },
    { field: 'updated_at', label: 'Updated' },
    { field: 'download', label: 'PDF', align: 'center', sortable: false },
  ];

  // Resolve hero to a preview URL for the summary card.
  let heroPreview = null;
  let heroUploadedLabel = null;
  if (project.hero) {
    if (project.hero.source === 'library') heroPreview = project.hero.file;
    else heroUploadedLabel = 'Uploaded image (server-side)';
  }

  return (
    <Grid container spacing={3}>
      {/* Left panel — summary + generate */}
      <Grid item xs={12} md={7}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h5" sx={{ color: NAVY, fontWeight: 300 }}>
              {project.client || 'Untitled client'}
            </Typography>
            <Typography variant="body2" color="text.secondary">{project.address_line1}</Typography>
            {project.address_line2 && (
              <Typography variant="body2" color="text.secondary">{project.address_line2}</Typography>
            )}

            {heroPreview && (
              <Box
                component="img"
                src={heroPreview}
                alt="Hero"
                sx={{ mt: 2, width: '100%', maxWidth: 260, aspectRatio: '16/9', objectFit: 'cover', borderRadius: 1, display: 'block' }}
              />
            )}
            {heroUploadedLabel && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {heroUploadedLabel}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1}>
              {(project.disciplines || []).map((d, i) => (
                <Stack key={`${d.id}-${i}`} direction="row" alignItems="baseline" spacing={2}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.title}</Typography>
                    {d.tag && (
                      <Typography variant="caption" color="text.secondary">{d.tag}</Typography>
                    )}
                  </Box>
                  <Typography variant="body2">{formatCurrency(d.subtotal)}</Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
                Grand total
              </Typography>
              <Typography variant="h4" sx={{ color: NAVY }}>
                {formatCurrency(project.grand_total)}
              </Typography>
            </Stack>

            {project.salesperson && (project.salesperson.name || project.salesperson.email) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
                  Next Step · contact
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {project.salesperson.name}
                </Typography>
                {project.salesperson.title && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {project.salesperson.title}
                  </Typography>
                )}
                {project.salesperson.email && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {project.salesperson.email}
                  </Typography>
                )}
                {project.salesperson.phone && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {project.salesperson.phone}
                  </Typography>
                )}
              </>
            )}

            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PictureAsPdfIcon />}
              disabled={generating}
              onClick={handleGenerate}
              sx={{ mt: 3, bgcolor: NAVY, '&:hover': { bgcolor: '#0d3560' }, textTransform: 'none' }}
            >
              {generating ? 'Generating…' : 'Generate PDF'}
            </Button>

            {genError && <Alert severity="error" sx={{ mt: 2 }}>{genError}</Alert>}

            {generatedBlobUrl && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Executive summary generated{summaryId ? ` (id ${summaryId})` : ''}.
                </Alert>
                <Button
                  component="a"
                  href={generatedBlobUrl}
                  download={`Executive_Summary_${slugify(project.client)}_${todayIso()}.pdf`}
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{ textTransform: 'none', color: NAVY, borderColor: NAVY, mb: 2 }}
                >
                  Download PDF
                </Button>
                <Box
                  component="iframe"
                  title="Executive summary preview"
                  src={generatedBlobUrl}
                  sx={{ width: '100%', minHeight: 700, border: '1px solid', borderColor: 'divider' }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Right panel — shared library of saved summaries */}
      <Grid item xs={12} md={5}>
        <Typography variant="overline" sx={{ color: NAVY, letterSpacing: '0.14em', fontWeight: 500 }}>
          Saved summaries (all users)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Every executive summary generated in Command Center. Click PDF to download.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell key={c.field} align={c.align || 'left'}>
                    {c.sortable === false ? (
                      c.label
                    ) : (
                      <TableSortLabel
                        active={sortField === c.field}
                        direction={sortField === c.field ? sortDir : 'asc'}
                        onClick={() => handleSort(c.field)}
                      >
                        {c.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDrafts.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.client}</TableCell>
                  <TableCell>{r.project_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.created_by_name || '—'}</Typography>
                    {r.created_by_email && (
                      <Typography variant="caption" color="text.secondary">
                        {r.created_by_email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(r.grand_total)}</TableCell>
                  <TableCell>{String(r.updated_at || '').slice(0, 10)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Download PDF">
                      <IconButton
                        onClick={() => handleDownloadDraft(r.id)}
                        size="small"
                        sx={{ color: NAVY }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {sortedDrafts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length}>
                    <Typography variant="body2" color="text.secondary">
                      No summaries yet. Generate one on the left to seed the library.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
}

// -------------------------------------------------------------
// Root wizard
// -------------------------------------------------------------
export default function Wizard({ editSummaryId, editNonce, onEditConsumed }) {
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 state
  const [parsedFiles, setParsedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Step 4 state — drafts list (lifted so it survives step navigation).
  const [drafts, setDrafts] = useState([]);

  // When rehydrating from a saved summary (Library → Edit), we mark that
  // we're in edit mode so the Generate button can UPDATE the existing row
  // instead of always creating a new one. `editingSummaryId` is the DB id.
  const [editingSummaryId, setEditingSummaryId] = useState(null);
  const [rehydrateStatus, setRehydrateStatus] = useState(null); // 'loading' | 'error' | null

  // Project payload (built incrementally as we walk through steps)
  const [project, setProject] = useState({
    client: '',
    address_line1: '',
    address_line2: '',
    cover_eyebrow: DEFAULT_COVER_EYEBROW,
    grand_total: 0,
    grand_total_manual: false,
    disciplines: [],
    hero: null,
    salesperson: { name: '', title: '', email: '', phone: '' },
  });

  // Rehydrate from a saved summary when the parent hands us an editSummaryId.
  // editNonce lets the parent force a re-load if the user clicks Edit twice
  // on the same row after abandoning a previous edit.
  useEffect(() => {
    if (!editSummaryId) return;
    let cancelled = false;
    setRehydrateStatus('loading');
    api.get(`/exec-summary/${editSummaryId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const payload = (data && data.payload) || {};
        // Rebuild the wizard's project state from the saved payload. The
        // payload was written by buildPayload() at generate time — same
        // shape. Fill in defaults for anything missing.
        setProject({
          client:        payload.client        || '',
          address_line1: payload.address_line1 || '',
          address_line2: payload.address_line2 || '',
          cover_eyebrow: payload.cover_eyebrow || DEFAULT_COVER_EYEBROW,
          grand_total:   Number(payload.grand_total) || 0,
          grand_total_manual: !!payload.grand_total_manual,
          disciplines:   Array.isArray(payload.disciplines) ? payload.disciplines : [],
          hero:          payload.hero || null,
          salesperson:   payload.salesperson || { name: '', title: '', email: '', phone: '' },
          // Preserve any other fields the payload carries (about copy, etc)
          ...Object.fromEntries(
            Object.entries(payload).filter(([k]) =>
              !['client','address_line1','address_line2','cover_eyebrow',
                'grand_total','grand_total_manual','disciplines','hero',
                'salesperson'].includes(k))),
        });
        // Jump the user to Step 2 (Header + disciplines) so they can review
        // and re-edit content without having to re-upload the PDFs. From
        // there they can Next through to Imagery + Review.
        setActiveStep(1);
        setEditingSummaryId(editSummaryId);
        setRehydrateStatus(null);
        if (onEditConsumed) onEditConsumed();
      })
      .catch((e) => {
        if (cancelled) return;
        setRehydrateStatus('error');
        console.error('rehydrate failed', e);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSummaryId, editNonce]);

  const handleAutoPopulate = useCallback((items) => {
    const ok = items.filter((f) => f.ok !== false);
    if (ok.length === 0) return;
    const first = ok[0];

    setProject((p) => {
      // Merge parsed proposals into the existing disciplines list rather than
      // overwriting it. This means users can upload PDFs one at a time (or in
      // batches) and every discipline shows up in the final summary.
      const byId = new Map();
      for (const d of (p.disciplines || [])) {
        if (d && d.id) byId.set(d.id, { ...d });
      }
      for (const f of ok) {
        // Skip proposals the server couldn't classify. Creating a discipline
        // row from an 'unknown' parse produces a page with a garbled title
        // and an empty body — which is exactly what Marc saw on 26-0511.
        // Better to surface it as an upload error and let sales retry.
        if (f.discipline === 'unknown' || !f.discipline) {
          continue;
        }
        const id = inferDisciplineId(f.discipline) || slugify(f.discipline || f.original_name);
        const template = DISCIPLINE_TEMPLATES[id] || {
          id,
          eyebrow: f.discipline || '',
          title: f.discipline || 'System',
          tag: '',
          subtotal: 0,
        };
        const subtotal = Number(f.grand_total) || 0;
        const existing = byId.get(id);
        // Merge server-side auto-extracted content into the discipline row.
        // Server may return `parsed` (structured discipline data) and
        // `suggestions` (renderer-ready fields). Both are optional.
        const parsed = f.parsed || {};
        const suggestions = f.suggestions || {};
        const fromParse = {};
        // AV/IT and Shades: normalize server-side rooms into the renderer's schema.
        // Lighting/Fixtures have no rooms‑page in the deck, so we skip.
        if (Array.isArray(parsed.rooms) && parsed.rooms.length && (id === 'avit' || id === 'shades')) {
          fromParse.rooms = normalizeRooms(id, parsed.rooms);
        }
        // Proposal ref — prefer the parser's, fall back to the top-level or header value.
        const propRef = parsed.proposal_ref
          || f.proposal_number
          || (f.header && f.header.proposal_number);
        if (propRef) fromParse.proposal_ref = propRef;
        if (Array.isArray(suggestions.stats) && suggestions.stats.length) fromParse.stats = suggestions.stats;
        if (Array.isArray(suggestions.system_items) && suggestions.system_items.length) fromParse.system_items = suggestions.system_items;
        if (Array.isArray(suggestions.summary_strip) && suggestions.summary_strip.length) fromParse.summary_strip = suggestions.summary_strip;
        if (suggestions.experience_lede) fromParse.experience_lede = suggestions.experience_lede;

        if (!existing) {
          byId.set(id, { ...template, ...fromParse, subtotal });
        } else {
          // Preserve existing user-edited fields; fill in only what the user
          // hasn't touched. For subtotal, latest/largest wins.
          const merged = { ...existing };
          for (const [k, v] of Object.entries(fromParse)) {
            const cur = existing[k];
            const emptyArray = Array.isArray(cur) && cur.length === 0;
            const emptyString = typeof cur === 'string' && !cur.trim();
            if (cur == null || emptyArray || emptyString) merged[k] = v;
          }
          if (subtotal > (Number(existing.subtotal) || 0)) merged.subtotal = subtotal;
          byId.set(id, merged);
        }
      }
      const disciplines = Array.from(byId.values());
      const gt = disciplines.reduce((a, d) => a + (Number(d.subtotal) || 0), 0);

      // Address fields — prefer the header from the first parsed proposal.
      const hdr = (first.header && typeof first.header === 'object') ? first.header : {};
      const parsedClient  = first.client        || hdr.client        || '';
      const parsedAddr1   = first.address_line1 || hdr.address_line1 || '';
      const parsedAddr2   = first.address_line2 || hdr.address_line2 || '';

      return {
        ...p,
        client:        p.client        || parsedClient,
        address_line1: p.address_line1 || parsedAddr1,
        address_line2: p.address_line2 || parsedAddr2,
        disciplines:   disciplines.length > 0 ? disciplines : p.disciplines,
        grand_total:   p.grand_total_manual ? p.grand_total : gt,
      };
    });
  }, []);

  // Missing imagery report — shared by Step 3's gate and its inline warning.
  // A discipline needs an image UNLESS it's flagged `no_equipment` (currently
  // just 'fixtures', which renders as a scope-only page with no hero art).
  const missingImagery = useMemo(() => {
    const missing = { hero: false, disciplines: [] };
    if (!project.hero || !project.hero.source) missing.hero = true;
    for (const d of (project.disciplines || [])) {
      if (d.no_equipment) continue;
      const img = d.image || {};
      if (!img.source) missing.disciplines.push(d.title || d.id);
    }
    return missing;
  }, [project.hero, project.disciplines]);

  const canGoNext = useMemo(() => {
    if (activeStep === 0) {
      // If we're editing a saved summary the disciplines are already loaded;
      // we don't need parsedFiles to advance. Otherwise require at least one
      // successfully parsed PDF.
      if (editingSummaryId && (project.disciplines || []).length > 0) return true;
      return parsedFiles.some((f) => f.ok !== false);
    }
    if (activeStep === 1) {
      const sp = project.salesperson || {};
      return (
        !!project.client &&
        !!project.address_line1 &&
        project.disciplines.length > 0 &&
        project.disciplines.some((d) => d.title && Number(d.subtotal) > 0) &&
        !!sp.name &&
        !!sp.email
      );
    }
    if (activeStep === 2) {
      // Every discipline (except no-equipment ones like Lighting Fixtures)
      // must have an image chosen or uploaded, and the cover needs a hero.
      return !missingImagery.hero && missingImagery.disciplines.length === 0;
    }
    return true;
  }, [activeStep, parsedFiles, project, missingImagery, editingSummaryId]);

  const handleReset = () => {
    setActiveStep(0);
    setParsedFiles([]);
    setUploadError(null);
    setProject({
      client: '', address_line1: '', address_line2: '',
      cover_eyebrow: DEFAULT_COVER_EYEBROW,
      grand_total: 0, grand_total_manual: false,
      disciplines: [], hero: null,
      salesperson: { name: '', title: '', email: '', phone: '' },
    });
  };

  return (
    <Box>
      {rehydrateStatus === 'loading' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loading saved summary…
        </Alert>
      )}
      {rehydrateStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Couldn’t load that summary for editing. Try again from the Library tab.
        </Alert>
      )}
      {editingSummaryId && (
        <Alert
          severity="info"
          icon={<EditIcon fontSize="small" />}
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => { setEditingSummaryId(null); handleReset(); }}
              sx={{ textTransform: 'none' }}
            >
              Start new instead
            </Button>
          }
        >
          <Typography variant="body2">
            <b>Editing an existing summary</b> for {project.client || 'this client'}. Your changes
            will save as a new revision when you regenerate. Uploaded proposals are optional — you
            can jump straight to Header &amp; disciplines or Imagery.
          </Typography>
        </Alert>
      )}
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          '& .MuiStepLabel-label': {
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            fontSize: 12,
            fontWeight: 500,
          },
          '& .MuiStepLabel-label.Mui-active': { color: NAVY },
          '& .MuiStepLabel-label.Mui-completed': { color: NAVY },
          '& .MuiStepIcon-root.Mui-active': { color: NAVY },
          '& .MuiStepIcon-root.Mui-completed': { color: NAVY },
        }}
      >
        {STEPS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Card variant="outlined" sx={{ borderColor: 'divider' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {activeStep === 0 && (
            <Step1Upload
              parsedFiles={parsedFiles}
              setParsedFiles={setParsedFiles}
              uploading={uploading}
              setUploading={setUploading}
              uploadError={uploadError}
              setUploadError={setUploadError}
              onAutoPopulate={handleAutoPopulate}
            />
          )}
          {activeStep === 1 && <Step2Header project={project} setProject={setProject} />}
          {activeStep === 2 && (
            <Step3Imagery
              project={project}
              setProject={setProject}
              missingImagery={missingImagery}
            />
          )}
          {activeStep === 3 && (
            <Step4Review
              project={project}
              drafts={drafts}
              setDrafts={setDrafts}
              parsedFiles={parsedFiles}
            />
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          onClick={handleReset}
          startIcon={<ResetIcon />}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Start over
        </Button>

        <Stack direction="row" spacing={1}>
          <Button
            disabled={activeStep === 0}
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disabled={!canGoNext || activeStep === STEPS.length - 1}
            onClick={() => setActiveStep((s) => Math.min(STEPS.length - 1, s + 1))}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0d3560' }, textTransform: 'none' }}
          >
            Next
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
